import React from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@musa.tech/design-system';
import { cn } from '@/lib/utils';

const MIN_MONTH = '2026-01';

interface PeriodPickerProps {
  value: string[];
  onChange: (months: string[]) => void;
  maxMonth?: string;
}

function shiftMonth(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7);
}

function buildShortcut(base: string, count: number, availableSet: Set<string>): string[] {
  const months: string[] = [];
  for (let i = 0; i < count; i++) {
    const m = shiftMonth(base, -i);
    if (availableSet.has(m)) months.push(m);
  }
  return months.sort();
}

function generateAvailableMonths(maxMonth: string): string[] {
  const months: string[] = [];
  let cursor = MIN_MONTH;
  while (cursor <= maxMonth) {
    months.push(cursor);
    cursor = shiftMonth(cursor, 1);
  }
  return months.reverse();
}

function formatMonthLabel(month: string): string {
  return format(parseISO(`${month}-01`), 'MMM/yy', { locale: ptBR });
}

export const PeriodPicker: React.FC<PeriodPickerProps> = ({ value, onChange, maxMonth }) => {
  const base = maxMonth ?? shiftMonth(new Date().toISOString().slice(0, 7), -1);
  const availableMonths = generateAvailableMonths(base);
  const availableSet = new Set(availableMonths);
  const selectedSet = new Set(value);

  const shortcuts = [
    { label: 'Últimos 3 meses', count: 3, months: buildShortcut(base, 3, availableSet) },
    { label: 'Últimos 6 meses', count: 6, months: buildShortcut(base, 6, availableSet) },
    { label: 'Último ano', count: 12, months: buildShortcut(base, 12, availableSet) },
  ].filter((sc) => availableMonths.length >= sc.count);

  const isShortcutActive = (sc: { count: number; months: string[] }) => {
    // Só ativo se o atalho entregou todos os meses que prometeu (dados suficientes)
    if (sc.months.length !== sc.count) return false;
    if (sc.months.length !== value.length) return false;
    const sSet = new Set(sc.months);
    return value.every((m) => sSet.has(m));
  };

  const handleToggle = (month: string) => {
    if (selectedSet.has(month)) {
      if (value.length <= 1) return;
      onChange(value.filter((m) => m !== month));
    } else {
      onChange([...value, month].sort());
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-1.5">
        {shortcuts.map((sc) => (
          <Button
            key={sc.label}
            variant={isShortcutActive(sc) ? 'default' : 'outline'}
            size="sm"
            className={cn('justify-start w-full', isShortcutActive(sc) && 'font-medium')}
            onClick={() => onChange(sc.months)}
            disabled={sc.months.length === 0}
          >
            {sc.label}
          </Button>
        ))}
      </div>

      <div className="border-t border-border" />

      <div className="max-h-32 overflow-y-auto rounded-md border border-border p-1.5">
      <div className="grid grid-cols-3 gap-1.5">
        {availableMonths.map((month) => (
          <Button
            key={month}
            variant={selectedSet.has(month) ? 'default' : 'outline'}
            size="sm"
            className={cn(
              'h-auto py-1.5 text-xs capitalize',
              selectedSet.has(month) && 'font-medium',
            )}
            onClick={() => handleToggle(month)}
          >
            {formatMonthLabel(month)}
          </Button>
        ))}
      </div>
      </div>
    </div>
  );
};
